import { useEffect, useRef, useState } from 'react';
import { employeeApi } from '../../../lib/api';
import { deviceMeta } from '../../../lib/photo';
import { errorMessage } from '../../../components/ErrorModalBody';

function nowISO() { return new Date().toISOString(); }

export function SignatureCapture({ onSaved }: { onSaved: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const lastRef = useRef<{ x: number; y: number } | null>(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (preview) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.4; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#1B1235';

    function pos(e: MouseEvent | TouchEvent) {
      const r = canvas!.getBoundingClientRect();
      const t = 'touches' in e ? e.touches[0] : e as MouseEvent;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    }
    function start(e: MouseEvent | TouchEvent) { drawingRef.current = true; setHasDrawn(true); lastRef.current = pos(e); e.preventDefault(); }
    function move(e: MouseEvent | TouchEvent) {
      if (!drawingRef.current) return;
      const p = pos(e);
      ctx.beginPath(); ctx.moveTo(lastRef.current!.x, lastRef.current!.y); ctx.lineTo(p.x, p.y); ctx.stroke();
      lastRef.current = p; e.preventDefault();
    }
    function end() { drawingRef.current = false; }

    canvas.onmousedown = start as any; canvas.onmousemove = move as any; window.onmouseup = end;
    canvas.ontouchstart = start as any; canvas.ontouchmove = move as any; canvas.ontouchend = end;
    return () => { window.onmouseup = null; };
  }, [preview]);

  function clear() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    setError('');
  }

  function confirmPreview() {
    if (!hasDrawn) { setError('Desenhe sua assinatura antes de continuar.'); return; }
    setPreview(canvasRef.current!.toDataURL('image/png'));
  }

  async function save() {
    if (!preview) return;
    setBusy(true);
    setError('');
    try {
      await employeeApi.saveSignature({ imagem: preview, criadaEm: nowISO(), device: deviceMeta() });
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (preview) {
    return (
      <>
        <h2>Confirme sua assinatura</h2>
        <p className="hint">A assinatura ficou boa? Depois de confirmada, ela passa a valer para todos os seus comprovantes e documentos.</p>
        <div style={{ border: '1.5px solid var(--line)', borderRadius: 12, padding: 10, background: '#fff', textAlign: 'center' }}>
          <img src={preview} style={{ maxWidth: '100%', maxHeight: 150 }} />
        </div>
        {error && <div className="err">{error}</div>}
        <div className="row" style={{ marginTop: 14 }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setPreview(null)}>Refazer</button>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy} onClick={save}>Confirmar assinatura</button>
        </div>
      </>
    );
  }

  return (
    <>
      <h2>Sua assinatura</h2>
      <p className="hint">Antes do seu primeiro registro, desenhe sua assinatura no quadro abaixo com o dedo. Você só faz isso uma vez — ela será usada em todos os seus comprovantes e documentos.</p>
      <div style={{ border: '1.5px solid var(--line-strong)', borderRadius: 12, overflow: 'hidden', touchAction: 'none' }}>
        <canvas ref={canvasRef} style={{ width: '100%', height: 160, background: '#fff', display: 'block' }} />
      </div>
      <div className="row" style={{ marginTop: 10 }}>
        <button className="btn btn-ghost" style={{ flex: 1 }} onClick={clear}>Limpar</button>
        <button className="btn btn-primary" style={{ flex: 1 }} onClick={confirmPreview}>Usar esta assinatura</button>
      </div>
      {error && <div className="err">{error}</div>}
    </>
  );
}
